import React, { useEffect, useRef, useState } from "react";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import IConnection_TokenUI from "../../../../models/IConnection_TokenUI";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import UserTypesEnum from "../../../../models/UserTypesEnum";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import IOnGetVendorsDTO from "../../../../models/DTO/IOnGetVendorsDTO";
import WhatsThis from "../../../shared/WhatsThis/WhatsThis";
import IGetVendorsDTO from "../../../../models/DTO/IGetVendorsDTO";
import IOnUpdateTokenConfigurationDTO from "../../../../models/DTO/IOnUpdateTokenConfigurationDTO";
import IOnGenerateVendorUserTokenDTO from "../../../../models/DTO/IOnGenerateVendorUserTokenDTO";
import IGenerateVendorUserTokenDTO from "../../../../models/DTO/IGenerateVendorUserTokenDTO";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import IUpdateTokenConfigurationDTO from "../../../../models/DTO/IUpdateTokenConfigurationDTO";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import IVendorModel from "../../../../models/IVendorModel";
import Typeahead from "../../../shared/Typeahead/Typeahead";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import IGetVendorAddressesDTO from "../../../../models/DTO/IGetVendorAddressesDTO";
import IOnGetVendorAddressesDTO from "../../../../models/DTO/IOnGetVendorAddressesDTO";
import IAddress from "../../../../models/IAddress";
import OwStaticTypeahead from "../../../shared/OwStaticTypeahead/OwStaticTypeahead";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";
import { useAppContextNonNull } from "@contexts/AppContext";
import CatchwerksService from "@services/CatchwerksService";

type Props = {
    cancel: () => void;
    foundVendors?: IVendorModel[];
    userToken?: IConnection_TokenUI;
};

const VendorUserSidebar = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();

    const formEl = useRef<HTMLFormElement | null>(null);

    const [clearVendorTypeahead, setClearVendorTypeahead] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [userTypes, setUserTypes] = useState([
        { value: UserTypesEnum.ADMIN, title: "Admin" },
        { value: UserTypesEnum.SUPPORT, title: "Support" },
    ]);
    const [createSSOUser, setCreateSSOUser] = useState(!!supplierModel.has_sso);
    const [vendorAddresses, setVendorAddresses] = useState<IAddress[]>([]);
    const [foundVendors, setFoundVendors] = useState<IVendorModel[] | undefined>();
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel | null | undefined>(null);
    const [userTypeId, setUserTypeId] = useState<number | undefined | null>(props.userToken?.user_type_id);
    const [email, setEmail] = useState("");
    const [name, setName] = useState(props.userToken?.User?.name || "");
    const [selectedShipTo, setSelectedShipTo] = useState<IAddress | undefined>();
    const [header, setHeader] = useState(props.userToken ? "update_vendor_user" : "create_vendor_user");
    const [catchwerksPhone, setCatchwerksPhone] = useState("");
    const [catchwerksConsent, setCatchwerksConsent] = useState(false);
    const catchwerksPhoneRef = useRef(catchwerksPhone);
    const catchwerksConsentRef = useRef(catchwerksConsent);

    useEffect(() => {
        catchwerksPhoneRef.current = catchwerksPhone;
    }, [catchwerksPhone]);
    useEffect(() => {
        catchwerksConsentRef.current = catchwerksConsent;
    }, [catchwerksConsent]);

    const onUpdateTokenConfiguration = (data: IOnUpdateTokenConfigurationDTO) => {
        if (data) {
            if (catchwerksPhoneRef.current && catchwerksConsentRef.current && supplierModel.Supplier_Integration.has_catchwerks) {
                CatchwerksService.enrollUser(
                    {
                        userGuid: props.userToken!.User!.guid,
                        phone: catchwerksPhoneRef.current,
                        verbalConsent: true,
                    },
                    userModel,
                    supplierModel
                );
            }
            props.cancel();
        }
    };

    const onGenerateVendorUserToken = (data: IOnGenerateVendorUserTokenDTO) => {
        if (data) {
            props.cancel();
        }
    };

    const onGetVendorAddresses = (data: IOnGetVendorAddressesDTO) => {
        setVendorAddresses(data.addresses);
    };

    const onGetVendors = (data: IOnGetVendorsDTO) => {
        const _foundVendors = data.supplierVendorTokens
            .filter((token) => token.Vendor)
            .map((tokenData) => {
                return tokenData.Vendor!;
            });

        setFoundVendors(_foundVendors);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.VendorsOnGetVendors, fn: onGetVendors },
        { endpoint: EndpointsEnum.TokenOnGenerateVendorUserToken, fn: onGenerateVendorUserToken },
        { endpoint: EndpointsEnum.TokenOnUpdateTokenConfiguration, fn: onUpdateTokenConfiguration },
        { endpoint: EndpointsEnum.AddressOnGetVendorAddresses, fn: onGetVendorAddresses },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getVendorAddress();
        checkFormValidity();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        checkFormValidity();
    }, [selectedVendor, email, name, userTypeId, selectedShipTo]);

    const getVendorAddress = () => {
        if (props.userToken?.Vendor) {
            const payload: IGetVendorAddressesDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: props.userToken?.Vendor?.guid,
            };

            socket.emit(EndpointsEnum.AddressGetVendorAddresses, payload);
        }
    };

    const findVendorsByName = (typeahead: string) => {
        const encodedTypeahead = encodeURIComponent(typeahead);

        const payload: IGetVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: `name=${encodedTypeahead}`,
        };

        socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    const selectVendor = (vendor: IVendorModel) => {
        setSelectedVendor(vendor);
    };

    const cancel = () => {
        props.cancel();
    };

    const clearTypeaheadCallback = () => {
        setSelectedVendor(null);
    };

    const checkFormValidity = () => {
        if (formEl?.current) {
            const _isFormValid = formEl.current.checkValidity();
            setIsFormValid(_isFormValid);
        }
    };

    const handleChange = (e: any) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "name":
                setName(value);
                break;

            case "email":
                setEmail(value);
                break;

            case "userTypeId":
                setUserTypeId(value);
                break;

            case "createSSOUser":
                setCreateSSOUser(value);
                break;

            case "catchwerksPhone":
                setCatchwerksPhone(value);
                break;
        }
    };

    const saveUser = () => {
        if (props.userToken) {
            const payload: IUpdateTokenConfigurationDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                tokenGuid: props.userToken.guid,
                user_type_id: userTypeId || UserTypesEnum.SUPPORT,
            };

            if (selectedShipTo) {
                payload.default_ship_to_id = selectedShipTo.id;
            }

            socket.emit(EndpointsEnum.TokenUpdateTokenConfiguration, payload);
        } else {
            if (selectedVendor && email) {
                const payload: IGenerateVendorUserTokenDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    recipient: email,
                    vendorGuid: selectedVendor.guid,
                    user_type_id: userTypeId || UserTypesEnum.SUPPORT,
                };

                if (name) {
                    payload.name = name;
                    payload.createSSOUser = createSSOUser;
                }

                socket.emit(EndpointsEnum.TokenGenerateVendorUserToken, payload);
            }
        }
    };

    const selectShipTo = (address: IAddress) => {
        setSelectedShipTo(address);
    };

    return (
        <div className="VendorUserSidebar" style={{ height: "100vh", overflow: "hidden" }}>
            <div className={"p-3"} style={{ height: "15vh" }}>
                <div className="text-end">
                    <button className="btn btn-danger btn-sm" onClick={cancel}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle
                    className={"mb-2"}
                    leftIconName={"fas fa-user"}
                    title={header}
                    supplierModel={supplierModel}
                    isSupplierVendorOverride={true}
                />
                <hr />
            </div>

            <div className={"p-3"} style={{ height: "85vh", overflowY: "scroll" }}>
                <form ref={formEl}>
                    <div className={"mt-2"}>
                        <OwLabel
                            title={"vendor"}
                            supplierModel={supplierModel}
                            isSupplierVendorOverride={true}
                            noBottomMargin={true}
                            showIsReqAsterik={true}
                            postText={":"}
                            className={"me-2"}
                        />
                        {props.userToken ? (
                            props.userToken.Vendor?.name
                        ) : (
                            <Typeahead
                                className=""
                                clearTypeahead={clearVendorTypeahead}
                                clearTypeaheadCallback={() => {
                                    clearTypeaheadCallback();
                                }}
                                typeaheadItems={foundVendors}
                                titleProp="name"
                                subTitleProp=""
                                maxResults={5}
                                placeholder="Search"
                                selectedItem={selectedVendor}
                                isSelectableFn={() => {
                                    return true;
                                }}
                                selectedItemFn={selectVendor}
                                typeaheadApiFn={findVendorsByName}
                            />
                        )}
                    </div>

                    {supplierModel.has_sso ? (
                        <div className={"mt-3"}>
                            <div>
                                <OwCheckbox
                                    name={"createSSOUser"}
                                    label={"create_sso_user"}
                                    fn={handleChange}
                                    checked={createSSOUser}
                                    truthyVal={true}
                                    falsyVal={false}
                                />
                                <div className={"w-20r"}>
                                    <WhatsThis subject={"whats_this_create_sso_user"} />
                                </div>
                            </div>
                            <div>
                                <OwLabel title={"name"} noBottomMargin={true} showIsReqAsterik={true} postText={":"} className={"me-2"} />
                                {props.userToken ? name : <OwInputWrapper fn={handleChange} name={"name"} value={name} isRequired={true} />}
                            </div>
                        </div>
                    ) : null}

                    <div className={"mt-2"}>
                        <OwLabel title={"email"} noBottomMargin={true} showIsReqAsterik={true} postText={":"} className={"me-2"} />
                        {props.userToken ? (
                            props.userToken.User?.email
                        ) : (
                            <OwInputWrapper
                                placeholder={"email"}
                                name={"email"}
                                value={email}
                                fn={handleChange}
                                isRequired={true}
                                type={"email"}
                            />
                        )}
                    </div>

                    {createSSOUser ? <div></div> : null}

                    <div className={"mt-2"}>
                        <OwLabel title={"user_type"} noBottomMargin={true} showIsReqAsterik={true} />
                        <OwSelectOption
                            valueProp={"value"}
                            titleProp={"title"}
                            options={userTypes}
                            fn={handleChange}
                            name={"userTypeId"}
                            selectedOption={props.userToken}
                            selectedOptionProp={"user_type_id"}
                            showBlankFirst={true}
                            isRequired={true}
                        />
                    </div>

                    <div className={"mt-2"}>
                        <div>
                            <OwLabel title={"default_ship_to"} postText={":"} className={"me-2"} />
                            <div>{props.userToken?.Address?.name}</div>
                            <div>{props.userToken?.Address?.address_internal_id}</div>
                        </div>
                        <div>
                            <OwStaticTypeahead
                                isSelectableFn={() => true}
                                selectedItemValueProp={"id"}
                                selectedItemFn={selectShipTo}
                                selectedItemTitleProp={"name"}
                                typeaheadItems={vendorAddresses}
                                titleProp={"name"}
                                valueProp={"id"}
                            />
                        </div>
                    </div>

                    {supplierModel.Supplier_Integration.has_catchwerks ? (
                        <div className={"mt-2"}>
                            <OwLabel title={"catchwerks_mobile"} noBottomMargin={true} postText={":"} className={"me-2"} />
                            <OwInputWrapper name={"catchwerksPhone"} value={catchwerksPhone} fn={handleChange} type="tel" />
                            {catchwerksPhone ? (
                                <div className={"mt-2"}>
                                    <OwLabel title={"consent_script"} noBottomMargin={true} postText={":"} className={"me-2"} />
                                    <div className={"card border-info"} style={{ maxWidth: "20rem" }}>
                                        <div className={"card-body p-2"}>
                                            <OwLabel title={"catchwerks_consent_script"} />
                                        </div>
                                    </div>
                                    <OwCheckbox
                                        name={"catchwerksConsent"}
                                        label={"catchwerks_verbal_consent"}
                                        checked={catchwerksConsent}
                                        fn={() => setCatchwerksConsent(!catchwerksConsent)}
                                    />
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <div className={"mt-2"}>
                        <OwButton
                            className={"btn-success me-2"}
                            fn={() => {
                                saveUser();
                            }}
                            title={"save"}
                            disabled={!isFormValid}
                        />

                        <OwButton className={"btn-danger"} fn={cancel} title={"cancel"} />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VendorUserSidebar;
